// src/components/LeadForm.jsx (or your current path)
import React, { useState } from 'react';
import './lead form.css';
import logoImage from '../../assets/NewlogoRWE.png';

// 🔽 Firestore
import { db } from '../../firebase'; // change to '../firebase' if needed
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const LeadForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    countryCode: '+1',
    phone: '',
    email: '',
    marketingConsent: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Common country codes with phone formats
  const countryCodes = [
    { code: '+1', country: 'US/Canada', format: '(555) 123-4567' },
    { code: '+44', country: 'UK', format: '55 1234 5678' },
    { code: '+49', country: 'Germany', format: '30 12345678' },
    { code: '+33', country: 'France', format: '1 23 45 67 89' },
    { code: '+39', country: 'Italy', format: '123 456 7890' },
    { code: '+34', country: 'Spain', format: '123 45 67 89' },
    { code: '+31', country: 'Netherlands', format: '6 12345678' },
    { code: '+41', country: 'Switzerland', format: '12 345 67 89' },
    { code: '+43', country: 'Austria', format: '123 456789' },
    { code: '+45', country: 'Denmark', format: '12 34 56 78' },
    { code: '+46', country: 'Sweden', format: '12-345 67 89' },
    { code: '+47', country: 'Norway', format: '123 45 678' },
    { code: '+358', country: 'Finland', format: '12 345 6789' },
    { code: '+61', country: 'Australia', format: '412 345 678' },
    { code: '+64', country: 'New Zealand', format: '21 123 4567' },
    { code: '+81', country: 'Japan', format: '90-1234-5678' },
    { code: '+82', country: 'South Korea', format: '10-1234-5678' },
    { code: '+86', country: 'China', format: '138 0013 8000' },
    { code: '+91', country: 'India', format: '98765 43210' },
    { code: '+55', country: 'Brazil', format: '11 91234-5678' },
    { code: '+52', country: 'Mexico', format: '55 1234 5678' },
    { code: '+54', country: 'Argentina', format: '11 1234-5678' },
    { code: '+56', country: 'Chile', format: '9 1234 5678' },
    { code: '+57', country: 'Colombia', format: '300 123 4567' },
    { code: '+51', country: 'Peru', format: '987 654 321' },
    { code: '+27', country: 'South Africa', format: '82 123 4567' },
    { code: '+971', country: 'UAE', format: '50 123 4567' },
    { code: '+966', country: 'Saudi Arabia', format: '50 123 4567' },
    { code: '+65', country: 'Singapore', format: '9123 4567' },
    { code: '+60', country: 'Malaysia', format: '12-345 6789' },
    { code: '+66', country: 'Thailand', format: '81 234 5678' },
    { code: '+63', country: 'Philippines', format: '917 123 4567' },
    { code: '+84', country: 'Vietnam', format: '91 234 5678' },
    { code: '+62', country: 'Indonesia', format: '812-3456-7890' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Get placeholder for current country code
  const getCurrentPlaceholder = () => {
    const currentCountry = countryCodes.find(country => country.code === formData.countryCode);
    return currentCountry ? currentCountry.format : '(555) 123-4567';
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^[0-9\s\-\(\)]{7,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.marketingConsent) {
      newErrors.marketingConsent = 'Marketing consent is required to proceed';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      // 🔽 Write to Firestore -> triggers your Cloud Function (if enabled)
      await addDoc(collection(db, 'leads'), {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        countryCode: formData.countryCode,
        phone: formData.phone.trim(),
        fullPhone: `${formData.countryCode} ${formData.phone.trim()}`,
        email: formData.email.trim(),
        marketingConsent: formData.marketingConsent,
        consentTimestamp: formData.marketingConsent ? serverTimestamp() : null,
        source: 'website',
        createdAt: serverTimestamp(),
      });

      setFormData({ firstName: '', lastName: '', countryCode: '+1', phone: '', email: '', marketingConsent: true });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Lead submit failed:', err);
      console.error('Error details:', err.message, err.code);
      
      // Provide more specific error messages
      let errorMessage = 'Something went wrong. Please try again.';
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your connection and try again.';
      } else if (err.code === 'unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setErrors(prev => ({ ...prev, form: errorMessage }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => { if (e.target === e.currentTarget) onClose(); };
  const handleClose = () => { setIsSubmitted(false); onClose(); };

  if (!isOpen) return null;

  return (
    <div className="lead-form-overlay" onClick={handleOverlayClick}>
      <div className="lead-form-popup">
        {!isSubmitted ? (
          <>
            <button className="close-button" onClick={onClose}>×</button>

            <div className="popup-header">
              <h2>Get Started Today</h2>
              <p>Join thousands of investors building wealth through real estate tokenization</p>
            </div>

            <form className="popup-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={errors.firstName ? 'error' : ''}
                  />
                  {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={errors.lastName ? 'error' : ''}
                  />
                  {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <div className="phone-input-container">
                  <select
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleInputChange}
                    className="country-code-select"
                  >
                    {countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.code} {country.country}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={getCurrentPlaceholder()}
                    className={`phone-input ${errors.phone ? 'error' : ''}`}
                  />
                </div>
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="marketingConsent"
                    name="marketingConsent"
                    checked={formData.marketingConsent}
                    onChange={handleInputChange}
                    className="consent-checkbox"
                  />
                  <label htmlFor="marketingConsent" className="consent-label">
                    I consent to receive marketing communications from Real World Equity via email, phone, and SMS. 
                    I understand I can opt out at any time. <span className="required-asterisk">*</span>
                  </label>
                </div>
                {errors.marketingConsent && <span className="error-message">{errors.marketingConsent}</span>}
              </div>

              {errors.form && <p className="error-message">{errors.form}</p>}

              <button type="submit" className="submit-button" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Get Started'}
              </button>
            </form>
          </>
        ) : (
          <>
            <button className="close-button" onClick={handleClose}>×</button>
            <div className="thank-you-content">
              <h2>Thank you for reaching out!</h2>
              <p>We will be in touch soon.</p>
            </div>
            <div className="logo-container">
              <img src={logoImage} alt="Real World Equity" className="thank-you-logo" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadForm;